import { Box, Grid } from '@mui/material';
import { colors, iconBtnPosition, IconButton, Icons } from '../../../packages';

import BoxFrame from '../../../components/v2/BoxFrame';
import Header from '../../../components/v2/Header';
import Typography from '../../../components/v2/Typography';
import { HeadingFont, HeadingLevel, HeadingWeight } from '../Heading/index.types';
import { typography } from '../Typography/index.constant';
import Markdown from 'markdown-to-jsx';
import dayjs from '../../../utils/dayjs';
import { requestPDMPSummary } from '../../../services/reporting/reporting.service';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/modules/snackbar';
import { useState } from 'react';

const CheckPDMP = ({ patientState, summary, lastUpdated, uuid }) => {
  const dispatch = useDispatch();
  const [showRefresh, setShowRefresh] = useState(true);

  const handlePDMP = () => {
    const url =
      patientState === 'Connecticut'
        ? 'https://connecticut.pmpaware.net/login'
        : patientState === 'Texas'
        ? 'https://texas.pmpaware.net/login'
        : patientState === 'Florida'
        ? 'https://florida.pmpaware.net/login'
        : patientState === 'Virginia'
        ? 'https://virginia.pmpaware.net/login'
        : patientState === 'New Hampshire'
        ? 'https://newhampshire.pmpaware.net/login'
        : null;
    if (url) {
      window.open(url, '_blank');
    }
  };
  const isStateSupported =
    patientState && ['Connecticut', 'Texas', 'Florida', 'Virginia', 'New Hampshire'].includes(patientState);
  if (!isStateSupported) {
    return null;
  }
  const hasSummary = summary && summary.trim() !== '';

  const handleRefresh = async () => {
    try {
      const response = await requestPDMPSummary(uuid);
      if (response?.data?.success) {
        setShowRefresh(false);
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'PDMP summary refresh requested successfully.',
          }),
        );
      }
    } catch (error) {
      console.error('Error refreshing PDMP summary:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to refresh PDMP summary. Please try again later.',
        }),
      );
    }
  };

  const isRecent = lastUpdated ? dayjs().diff(dayjs(lastUpdated), 'hour') < 10 : false;

  return (
    <BoxFrame>
      <Grid container>
        <Grid item xs={12} md={9}>
          <Box py={2.5} px={3} flexDirection="column" display="flex">
            <Header
              label={hasSummary ? `${patientState} PDMP Summary` : `Check ${patientState} PDMP`}
              fontFamily={HeadingFont.PRIMARY}
              fontVariant={HeadingLevel.SUB}
              fontWeight={HeadingWeight.SEMI_BOLD}
            />
            {hasSummary && (
              <Box display="flex" alignItems="center">
                <Typography color={colors.neutral600} {...typography.body.s.medium}>
                  Last updated: {dayjs(lastUpdated).format('MM/DD/YYYY')}
                </Typography>
                {uuid && !isRecent && showRefresh && (
                  <IconButton iconPosition={iconBtnPosition.LEFT} onClick={handleRefresh}>
                    <Typography {...typography.body.xs.bold}>Refresh</Typography>
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={3} display="flex" justifyContent="flex-end" alignItems="center">
          <Box
            sx={{
              '& button': { gap: 1 },
              '& button svg': {
                width: 16,
                height: 20,
              },
            }}
            pr={2}
          >
            <IconButton
              icon="square-arrow-up-right"
              iconPosition={iconBtnPosition.RIGHT}
              onClick={() => handlePDMP()}
            >
              <Typography {...typography.body.s.bold}>{hasSummary ? 'View' : 'Check'} PDMP</Typography>
            </IconButton>
          </Box>
        </Grid>
        {hasSummary && (
          <Grid
            item
            container
            sx={{
              borderTop: `1px solid ${colors.neutral100}`,
              '& strong': {
                fontSize: '14px',
              },
              '&>div': {
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '24px',
                '& p': {
                  margin: 0,
                },
                '& ul': {
                  margin: 0,
                },
              },
            }}
          >
            <Markdown>{summary}</Markdown>
          </Grid>
        )}
      </Grid>
    </BoxFrame>
  );
};

export default CheckPDMP;
